import { useNavigate, useLocation } from "react-router-dom";
import { useState, createContext, useContext } from 'react';
import { Tooltip, Drawer, useMediaQuery, useTheme, Collapse } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Iconify from "src/components/Iconify";
import { verifyRules, validaMenu, verifyFeatures } from "src/utils/functions";
import { useSelector, useDispatch } from 'src/redux/store';
import { setChatInfo } from 'src/redux/slices/chat';
import { setMessages } from 'src/redux/slices/staff';
import useAuth from 'src/hooks/useAuth';
import Logo from 'src/components/Logo';
import NotificationsPopover from './NotificationsPopover';
import { DropdownAccountNav } from 'src/@/components/use/dropdownMenu/DropdownAccountNav';
import { HelpComponent } from 'src/sections/@global/help';
import { InternetConnectionAlertHeader } from 'src/sections/@global/connection/InternetConnectionAlertHeader';
import { ZuiTooltip } from 'src/@/components/use/tooltip';
import { getSpent } from 'src/helpers/cliente.helper';
import { useEffect } from 'react';

const SidebarContext = createContext({ isExpanded: false });

const SidebarMenuButton = ({ title, onClick, slug, icon, badge = 0, isSubItem = false }) => {
    const { isExpanded } = useContext(SidebarContext);
    const location = useLocation();
    const path = location.pathname.substring(1);
    const active = path.includes(slug);

    const ButtonContent = (
        <button
            onClick={onClick}
            className={`
                group relative flex w-full items-center gap-3 rounded-xl py-2 text-[12px] shadow-none
                transition-all duration-200 ease-in-out
                ${isExpanded ? 'px-3' : 'px-0 py-1 justify-center'}
                ${isSubItem && isExpanded ? 'pl-4' : ''}
                ${active
                    ? 'bg-white/15 text-white shadow-sm backdrop-blur-sm'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }
            `}
        >
            {icon && (
                <Iconify
                    icon={icon}
                    width={isSubItem ? 13 : 16}
                    height={isSubItem ? 13 : 16}
                    className={`
                        transition-colors duration-200 flex-shrink-0
                        ${active ? 'text-pink-300' : 'text-white/50 group-hover:text-white/80'}
                    `}
                />
            )}
            {isExpanded && (
                <>
                    <span className="truncate flex-1 text-left">{title}</span>
                    {badge > 0 && (
                        <span className={`
                            text-[11px] px-1 py-0.5 rounded-full
                            ${active ? 'bg-pink-500 text-white' : 'bg-white/20 text-white'}
                        `}>
                            {badge}
                        </span>
                    )}
                </>
            )}
            {!isExpanded && badge > 0 && (
                <span className="absolute -top-1 -right-1 text-[9px] px-1 py-0.5 rounded-full font-bold bg-purple-600 text-white min-w-[16px]">
                    {badge}
                </span>
            )}
        </button>
    );

    if (!isExpanded) {
        return (
            <Tooltip title={title} placement="right" arrow>
                {ButtonContent}
            </Tooltip>
        );
    }

    return ButtonContent;
};

const SidebarExpandableMenu = ({ title, icon, children, defaultOpen = false }) => {
    const { isExpanded } = useContext(SidebarContext);
    const [isOpen, setIsOpen] = useState(defaultOpen);

    // Se nao esta expandido, nao mostra o menu expansivel
    if (!isExpanded) {
        return null;
    }

    return (
        <div className="mb-1">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`
                    group relative flex w-full items-center gap-3 rounded-xl py-2.5
                    transition-all duration-200 ease-in-out px-3
                    text-white/70 hover:bg-white/10 hover:text-white
                `}
            >
                <span className="truncate flex-1 text-left">{title}</span>
                <Iconify
                    icon="lucide:chevron-down"
                    width={16}
                    className={`transition-transform duration-200 text-white/40 ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>
            <Collapse in={isOpen}>
                <div className="mt-1 space-y-0.5">
                    {children}
                </div>
            </Collapse>
        </div>
    );
};

const SidebarGroup = ({ children, label }) => {
    const { isExpanded } = useContext(SidebarContext);

    return (
        <div className="mb-4 last:mb-0">
            {label && isExpanded && (
                <div className="px-3 mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-white/40 text-sm">
                        {label}
                    </span>
                </div>
            )}
            <div className="space-y-0.5">
                {children}
            </div>
        </div>
    );
};

export default function MainSidebar() {
    const { user, cliente } = useAuth();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const { t } = useTranslation('navigation');
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { roomsWithNewMessages } = useSelector((state) => state.staff);
    const { flag } = useSelector((state) => state.global);

    // Por padrao, comeca colapsado (nao expandido)
    const [isExpanded, setIsExpanded] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [saldo, setSaldo] = useState(0);
    const [credit, setCredit] = useState(0);
    const [isLoadingSaldo, setIsLoadingSaldo] = useState(false);

    const soundNotification = localStorage.getItem('soundNotification');
    const isAdmin = user?.level === "root";

    const changeSoundNotification = (value) => {
        localStorage.setItem('soundNotification', value);
        window.location.reload();
    };

    async function handleGetSaldo() {
        try {
            setIsLoadingSaldo(true);
            const hoje = new Date();
            const startDate = hoje.toISOString().split('T')[0];
            const endDate = hoje.toISOString().split('T')[0];
            const querys = `?inicio=${startDate}&final=${endDate}&periodo=mes_atual`;
            const response = await getSpent(user.clienteId, querys);
            const limiteRestante = response?.data?.credits?.total?.limit - response?.data?.credits?.total?.use || 0;
            const saldoDisponivel = limiteRestante * 0.33;
            setSaldo(saldoDisponivel);
            setCredit(limiteRestante);
        } catch (error) {
            console.error('Erro ao buscar saldo:', error);
        } finally {
            setIsLoadingSaldo(false);
        }
    }

    useEffect(() => {
        handleGetSaldo();
    }, []);

    const handleNavigate = (path) => {
        navigate(path);
        if (isMobile) setMobileOpen(false);
    };

    const sidebarContent = (
        <aside
            className={`
                flex flex-col bg-gradient-to-b from-[#1a0a2e] via-[#160826] to-[#120620] dark:from-[#10051c] dark:via-[#0d0418] dark:to-[#0a0314] flex-shrink-0 h-screen relative z-20
                transition-all duration-300 ease-in-out
                ${isMobile ? 'w-[220px]' : (isExpanded ? 'w-[220px]' : 'w-[70px] pl-1')}
            `}
        >
            {/* Header com Logo */}
            <div className={`flex items-center h-14 border-b border-white/10 ${isExpanded ? 'px-4 justify-between' : 'px-2 justify-center'}`}>
                {isExpanded ? (
                    <>
                        <Logo />
                        {isMobile ? (
                            <button
                                onClick={() => setMobileOpen(false)}
                                className="p-1.5 rounded-md hover:bg-white/10 text-white/60"
                            >
                                <Iconify icon="heroicons:x-mark" width={20} />
                            </button>
                        ) : (
                            <button
                                onClick={() => setIsExpanded(false)}
                                className="p-2 rounded-md hover:bg-white/10 text-white/60"
                                title={t('navbar.collapseMenu')}
                            >
                                <Iconify icon="solar:alt-arrow-left-linear" width={18} />
                            </button>
                        )}
                    </>
                ) : (
                    <Tooltip title={t('navbar.expandMenu')} placement="right">
                        <button
                            onClick={() => setIsExpanded(true)}
                            className="p-2 rounded-md hover:bg-white/10"
                        >
                            <Iconify icon="tabler:layout-sidebar-left-expand-filled" width={22} className="text-white/60" />
                        </button>
                    </Tooltip>
                )}
            </div>

            {/* Flag de alerta */}
            {flag?.status && isExpanded && (
                <div className={`flex w-full bg-${flag?.flag}-600 py-2 items-center justify-center text-xs font-medium text-white px-2`}>
                    {flag?.msg}
                </div>
            )}

            {/* Menu de navegacao */}
            <div className={`flex flex-col flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-white/20 hover:scrollbar-thumb-white/30 ${isExpanded ? 'px-3' : 'px-2'}`}>
                <nav>
                    <SidebarGroup label={t('sidebar.groups.main')}>
                        {verifyRules(user.rules, "chats") && (
                            <SidebarMenuButton
                                title={t('sidebar.conversations')}
                                slug="chats/chat"
                                icon="lucide:message-circle"
                                onClick={() => {
                                    handleNavigate('/chats');
                                    dispatch(setChatInfo(null));
                                    dispatch(setMessages([]));
                                }}
                            />
                        )}

                        {verifyRules(user.rules, "chat_interno") && (
                            <SidebarMenuButton
                                title={t('sidebar.internalChat')}
                                slug="chats/interno"
                                icon="lucide:users-round"
                                badge={roomsWithNewMessages.length}
                                onClick={() => handleNavigate('/chats/interno')}
                            />
                        )}

                        {verifyRules(user.rules, "chamados") && (
                            <SidebarMenuButton
                                title={t('sidebar.funnel')}
                                slug="dashboard/negocios"
                                icon="lucide:filter"
                                onClick={() => handleNavigate('/dashboard/negocios')}
                            />
                        )}

                        <SidebarMenuButton
                            title={t('sidebar.contacts')}
                            slug="dashboard/contatos"
                            icon="lucide:contact"
                            onClick={() => handleNavigate('/dashboard/contatos')}
                        />

                        {verifyRules(user.rules, "chamados") && (
                            <SidebarMenuButton
                                title={t('sidebar.tickets')}
                                slug="dashboard/chamados"
                                icon="lucide:ticket"
                                onClick={() => handleNavigate('/dashboard/chamados-chat')}
                            />
                        )}

                        {verifyFeatures(cliente?.features, "calendarios") && (
                            <SidebarMenuButton
                                title={t('sidebar.calendar')}
                                slug="dashboard/calendario"
                                icon="lucide:calendar"
                                onClick={() => handleNavigate('/dashboard/calendario')}
                            />
                        )}

                        {verifyRules(user.rules, "vendas") && (
                            <SidebarMenuButton
                                title={t('sidebar.sales')}
                                slug="dashboard/vendas"
                                icon="lucide:shopping-bag"
                                onClick={() => handleNavigate('/dashboard/vendas')}
                            />
                        )}

                        {verifyRules(user.rules, "automacoes") && isExpanded && (
                            <SidebarMenuButton
                                title={t('sidebar.automations')}
                                slug="dashboard/automacoes"
                                icon="lucide:workflow"
                                onClick={() => handleNavigate('/dashboard/automacoes')}
                            />
                        )}

                        {isExpanded && (
                            <SidebarMenuButton
                                title={t('sidebar.forms')}
                                slug="dashboard/formularios"
                                icon="lucide:file-text"
                                onClick={() => handleNavigate('/dashboard/formularios')}
                            />
                        )}

                        {isExpanded && (
                            <SidebarMenuButton
                                title={t('sidebar.tasks')}
                                slug="dashboard/tarefas"
                                icon="lucide:check-square"
                                onClick={() => handleNavigate('/dashboard/tarefas')}
                            />
                        )}

                        {/* {isExpanded && (
                            <SidebarMenuButton
                                title="Plugins"
                                slug="dashboard/plugins"
                                icon="lucide:puzzle"
                                onClick={() => handleNavigate('/dashboard/plugins')}
                            />
                        )} */}
                    </SidebarGroup>

                    {/* Botão destacado IA
                    <div className={`my-3 ${isExpanded ? 'px-1' : 'px-0 flex justify-center'}`}>
                        <Tooltip title={!isExpanded ? 'Chat IA' : ''} placement="right">
                            <button
                                onClick={() => handleNavigate('/dashboard/painel-ia')}
                                className={`
                                    relative flex items-center gap-2.5 rounded-xl font-semibold
                                    bg-pink-500 hover:bg-pink-600 text-white shadow-lg shadow-pink-500/30
                                    ${isExpanded ? 'w-full px-3.5 py-2.5 text-[12px] justify-center' : 'w-10 h-10 justify-center'}
                                `}
                            >
                                <span className="absolute inset-0 rounded-xl bg-pink-400 opacity-20" />
                                <Iconify icon="lucide:sparkles" width={isExpanded ? 16 : 18} className="relative z-10 flex-shrink-0" />
                                {isExpanded && <span className="relative z-10">Chat IA</span>}
                            </button>
                        </Tooltip>
                    </div> */}

                     {(isAdmin || verifyRules(user.rules, 'lider_setor')) && (
                        <>
                            {isExpanded && <div className="my-3 mx-2 h-px bg-white/10" />}
                            <SidebarGroup className="text-[10px]" label={t('sidebar.groups.admin')}>
                                <SidebarMenuButton
                                    title={t('sidebar.metrics')}
                                    slug="dashboard/home"
                                    icon="tabler:brand-google-analytics"
                                    onClick={() => handleNavigate('/dashboard/home')}
                                />
                                <SidebarMenuButton
                                    title={t('sidebar.supervise')}
                                    slug="dashboard/supervisao"
                                    icon="lucide:eye"
                                    onClick={() => handleNavigate('/dashboard/supervisao/lista')}
                                />

                                {(verifyRules(user.rules, 'relatorios') || verifyRules(user.rules, 'lider_setor')) && (
                                    <SidebarExpandableMenu title={t('sidebar.reports')} icon="lucide:file-bar-chart">
                                        <SidebarMenuButton
                                            title={t('sidebar.reviews')}
                                            slug="dashboard/relatorios/chats-avaliacoes"
                                            icon="lucide:star"
                                            isSubItem
                                            onClick={() => handleNavigate('/dashboard/relatorios/chats-avaliacoes')}
                                        />
                                        <SidebarMenuButton
                                            title={t('sidebar.tickets')}
                                            slug="dashboard/relatorios/chamados"
                                            icon="lucide:clipboard-list"
                                            isSubItem
                                            onClick={() => handleNavigate('/dashboard/relatorios/chamados')}
                                        />
                                    </SidebarExpandableMenu>
                                )}

                                {user.level === 'root' && (
                                    <SidebarExpandableMenu title={t('sidebar.ai')} icon="lucide:brain">
                                        <SidebarMenuButton
                                            title={t('sidebar.serviceAnalysis')}
                                            slug="dashboard/ai/analises"
                                            icon="lucide:sparkles"
                                            isSubItem
                                            onClick={() => handleNavigate('/dashboard/ai/analises')}
                                        />
                                        {/* <SidebarMenuButton
                                            title="Chat IA Dashboard"
                                            slug="dashboard/painel-ia"
                                            icon="lucide:message-square-plus"
                                            isSubItem
                                            onClick={() => handleNavigate('/dashboard/painel-ia')}
                                        /> */}
                                    </SidebarExpandableMenu>
                                )}
                            </SidebarGroup>
                        </>
                    )}

                    {isExpanded && <div className="my-3 mx-2 h-px bg-white/10" />}

                    {/* Marketing */}
                    {validaMenu(user ? user.rules : [''], ['agendamentos', 'campanhas']) && (
                        <SidebarExpandableMenu title={t('sidebar.marketing')} icon="lucide:megaphone">
                            {verifyRules(user.rules, 'agendamentos') && (
                                <SidebarMenuButton
                                    title={t('sidebar.schedules')}
                                    slug="chats/agendamentos"
                                    icon="lucide:clock"
                                    isSubItem
                                    onClick={() => handleNavigate('/chats/agendamentos')}
                                />
                            )}
                            {verifyRules(user.rules, 'campanhas') && (
                                <SidebarMenuButton
                                    title={t('sidebar.campaigns')}
                                    slug="dashboard/campanhas"
                                    icon="lucide:send"
                                    isSubItem
                                    onClick={() => handleNavigate('/dashboard/campanhas')}
                                />
                            )}
                            {verifyRules(user.rules, 'campanhas') && (
                                <SidebarMenuButton
                                    title={t('sidebar.tracking')}
                                    slug="dashboard/marketing/rastreamento"
                                    icon="lucide:line-chart"
                                    isSubItem
                                    onClick={() => handleNavigate('/dashboard/marketing/rastreamento')}
                                />
                            )}
                        </SidebarExpandableMenu>
                    )}

                    {/* Admin */}
                   


                </nav>
            </div>

            {/* Footer com acoes */}
            <div className={`border-t border-white/10 py-3 ${isExpanded ? 'px-3' : 'px-2'}`}>

                {/* Creditos */}
                {isExpanded ? (
                    <ZuiTooltip
                        content={
                            <div className="space-y-1 p-1 mt-2">
                                <div className="font-semibold text-xs text-gray-900">{t('navbar.credits')}</div>
                                <div className="text-[10px] text-gray-500 space-y-0.5">
                                    <div>{t('navbar.balance')}: <span className="font-medium text-gray-700">{credit}</span> {t('navbar.credits').toLowerCase()}</div>
                                    <div>{t('navbar.status')}: <span className={saldo < 0 ? 'text-red-500' : 'text-green-600'}>{saldo < 0 ? t('common:status.negative') : t('common:status.positive')}</span></div>
                                </div>
                            </div>
                        }
                    >
                        <button
                            onClick={() => handleNavigate('/dashboard/conta/consumo')}
                            className={`
                                w-full flex items-center gap-2 px-3 py-2 rounded-md border transition-all duration-200 mb-2
                                ${saldo < 0
                                    ? 'bg-red-500/20 border-red-400/30 text-red-300'
                                    : 'bg-white/10 border-white/20 text-white/80 hover:bg-white/15'
                                }
                            `}
                        >
                            <Iconify icon="solar:wallet-bold-duotone" width={18} />
                            <span className="text-xs font-semibold flex-1 text-left">{credit} {t('navbar.credits').toLowerCase()}</span>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleGetSaldo();
                                }}
                                className="p-0.5 rounded-md hover:bg-white/10 disabled:opacity-50"
                                disabled={isLoadingSaldo}
                            >
                                <Iconify
                                    icon="lucide:refresh-cw"
                                    width={12}
                                    className={isLoadingSaldo ? 'animate-spin' : ''}
                                />
                            </button>
                        </button>
                    </ZuiTooltip>
                ) : (
                    <Tooltip title={`${credit} ${t('navbar.credits').toLowerCase()}`} placement="right">
                        <button
                            onClick={() => handleNavigate('/dashboard/conta/consumo')}
                            className={`
                                w-full flex flex-col items-center justify-center p-2.5 rounded-lg border transition-all duration-200 mb-2 gap-0.5
                                ${saldo < 0
                                    ? 'bg-red-500/20 border-red-400/30 text-red-300'
                                    : 'bg-white/10 border-white/20 text-white/80 hover:bg-white/15'
                                }
                            `}
                        >
                            <Iconify icon="solar:wallet-bold-duotone" width={18} />
                            <span className="text-[10px] font-bold">{credit}</span>
                        </button>
                    </Tooltip>
                )}

                {/* Configuracoes */}
                {verifyRules(user.rules, "configuracoes") && (
                    <>
                        {isExpanded && <div className="my-3 mx-2 h-px bg-white/10" />}
                        <SidebarMenuButton
                            title={t('sidebar.settings')}
                            slug="dashboard/configuracoes"
                            icon="lucide:settings"
                            onClick={() => handleNavigate('/dashboard/configuracoes/canais')}
                        />
                    </>
                )}

                {/* Acoes rapidas */}
                <div className={`flex ${isExpanded ? 'justify-between' : 'flex-col gap-2'} items-center mb-2 mt-2`}>
                    <Tooltip title={soundNotification === "true" ? t('navbar.soundOn') : t('navbar.soundOff')} placement={isExpanded ? "top" : "right"}>
                        <button
                            onClick={() => changeSoundNotification(soundNotification === "true" ? "false" : "true")}
                            className="p-2 rounded-lg text-white/50 hover:bg-white/10 hover:text-white/80 transition-colors"
                        >
                            <Iconify
                                icon={soundNotification === "true" ? "solar:volume-loud-bold-duotone" : "solar:volume-cross-bold-duotone"}
                                width={20}
                            />
                        </button>
                    </Tooltip>

                    <NotificationsPopover />
                    <HelpComponent />
                </div>

                <InternetConnectionAlertHeader />

                {/* Divider */}
                <div className="my-2 h-px bg-white/10" />

                {/* Conta do usuario */}
                <DropdownAccountNav variant={isExpanded ? "full" : "icon"} />
            </div>
        </aside>
    );

    // Mobile: header fixo com botao para abrir drawer
    if (isMobile) {
        return (
            <SidebarContext.Provider value={{ isExpanded: true }}>
                {/* Header mobile fixo */}
                <header className="fixed top-0 left-0 right-0 z-30 h-14 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 flex items-center px-4 justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setMobileOpen(true)}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-600 dark:text-gray-300"
                        >
                            <Iconify icon="solar:hamburger-menu-linear" width={24} />
                        </button>
                        <Logo />
                    </div>

                    <div className="flex items-center gap-2">
                        <NotificationsPopover />
                        <DropdownAccountNav variant="icon" />
                    </div>
                </header>

                {/* Drawer mobile - sempre expandido */}
                <Drawer
                    anchor="left"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    PaperProps={{
                        sx: {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                        }
                    }}
                >
                    <SidebarContext.Provider value={{ isExpanded: true }}>
                        <aside className="flex flex-col flex-shrink-0 border-r border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 h-screen relative z-20 w-[280px]">
                            {/* Header com Logo */}
                            <div className="flex items-center h-14 border-b border-gray-100 dark:border-neutral-800 px-4 justify-between">
                                <Logo />
                                <button
                                    onClick={() => setMobileOpen(false)}
                                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-500 dark:text-gray-400"
                                >
                                    <Iconify icon="heroicons:x-mark" width={20} />
                                </button>
                            </div>

                            {/* Flag de alerta */}
                            {flag?.status && (
                                <div className={`flex w-full bg-${flag?.flag}-600 py-2 items-center justify-center text-xs font-medium text-white px-2`}>
                                    {flag?.msg}
                                </div>
                            )}

                            {/* Menu de navegacao */}
                            <div className="flex flex-col flex-1 overflow-y-auto py-4 px-3 scrollbar-thin scrollbar-thumb-gray-100 hover:scrollbar-thumb-gray-200">
                                <nav>
                                    <SidebarGroup label={t('sidebar.groups.main')}>
                                        {verifyRules(user.rules, "chats") && (
                                            <SidebarMenuButton
                                                title={t('sidebar.conversations')}
                                                slug="chats/chat"
                                                icon="lucide:message-circle"
                                                onClick={() => {
                                                    handleNavigate('/chats');
                                                    dispatch(setChatInfo(null));
                                                    dispatch(setMessages([]));
                                                }}
                                            />
                                        )}

                                        {verifyRules(user.rules, "chat_interno") && (
                                            <SidebarMenuButton
                                                title={t('sidebar.internalChat')}
                                                slug="chats/interno"
                                                icon="lucide:users-round"
                                                badge={roomsWithNewMessages.length}
                                                onClick={() => handleNavigate('/chats/interno')}
                                            />
                                        )}

                                        {verifyRules(user.rules, "chamados") && (
                                            <SidebarMenuButton
                                                title={t('sidebar.funnel')}
                                                slug="dashboard/negocios"
                                                icon="lucide:filter"
                                                onClick={() => handleNavigate('/dashboard/negocios')}
                                            />
                                        )}

                                        <SidebarMenuButton
                                            title={t('sidebar.contacts')}
                                            slug="dashboard/contatos"
                                            icon="lucide:contact"
                                            onClick={() => handleNavigate('/dashboard/contatos')}
                                        />

                                        {verifyRules(user.rules, "chamados") && (
                                            <SidebarMenuButton
                                                title={t('sidebar.tickets')}
                                                slug="dashboard/chamados"
                                                icon="lucide:ticket"
                                                onClick={() => handleNavigate('/dashboard/chamados')}
                                            />
                                        )}

                                        {verifyRules(user.rules, "calendario") && verifyFeatures(cliente?.features, "lais_calendario") && (
                                            <SidebarMenuButton
                                                title={t('sidebar.calendar')}
                                                slug="dashboard/calendario"
                                                icon="lucide:calendar"
                                                onClick={() => handleNavigate('/dashboard/calendario')}
                                            />
                                        )}

                                        {verifyRules(user.rules, "vendas") && (
                                            <SidebarMenuButton
                                                title={t('sidebar.sales')}
                                                slug="dashboard/vendas"
                                                icon="lucide:shopping-bag"
                                                onClick={() => handleNavigate('/dashboard/vendas')}
                                            />
                                        )}

                                        {verifyRules(user.rules, "automacoes") && (
                                            <SidebarMenuButton
                                                title={t('sidebar.automations')}
                                                slug="dashboard/automacoes"
                                                icon="lucide:workflow"
                                                onClick={() => handleNavigate('/dashboard/automacoes')}
                                            />
                                        )}

                                        <SidebarMenuButton
                                            title="Plugins"
                                            slug="dashboard/plugins"
                                            icon="lucide:puzzle"
                                            onClick={() => handleNavigate('/dashboard/plugins')}
                                        />
                                    </SidebarGroup>

                                    {/* Botão destacado IA - mobile */}
                                    <div className="my-3 px-1">
                                        <button
                                            onClick={() => handleNavigate('/dashboard/painel-ia')}
                                            className="relative flex items-center gap-2.5 w-full px-3.5 py-2.5 rounded-xl text-[12px] font-semibold justify-center bg-pink-500 hover:bg-pink-600 text-white shadow-lg shadow-pink-500/30 transition-all duration-300 active:scale-95"
                                        >
                                            <span className="absolute inset-0 rounded-xl bg-pink-400 animate-ping opacity-20" />
                                            <Iconify icon="lucide:sparkles" width={16} className="relative z-10 flex-shrink-0" />
                                            <span className="relative z-10">Chat IA</span>
                                        </button>
                                    </div>

                                    <div className="my-3 mx-2 h-px bg-gray-100 dark:bg-neutral-800" />

                                    {/* Marketing */}
                                    {validaMenu(user ? user.rules : [''], ['agendamentos', 'campanhas']) && (
                                        <SidebarExpandableMenu title={t('sidebar.marketing')} icon="lucide:megaphone">
                                            {verifyRules(user.rules, 'agendamentos') && (
                                                <SidebarMenuButton
                                                    title={t('sidebar.schedules')}
                                                    slug="chats/agendamentos"
                                                    icon="lucide:clock"
                                                    isSubItem
                                                    onClick={() => handleNavigate('/chats/agendamentos')}
                                                />
                                            )}
                                            {verifyRules(user.rules, 'campanhas') && (
                                                <SidebarMenuButton
                                                    title={t('sidebar.campaigns')}
                                                    slug="dashboard/campanhas"
                                                    icon="lucide:send"
                                                    isSubItem
                                                    onClick={() => handleNavigate('/dashboard/campanhas')}
                                                />
                                            )}
                                            {verifyRules(user.rules, 'campanhas') && (
                                                <SidebarMenuButton
                                                    title={t('sidebar.tracking')}
                                                    slug="dashboard/marketing/rastreamento"
                                                    icon="lucide:line-chart"
                                                    isSubItem
                                                    onClick={() => handleNavigate('/dashboard/marketing/rastreamento')}
                                                />
                                            )}
                                        </SidebarExpandableMenu>
                                    )}

                                    {/* Admin */}
                                    {(isAdmin || verifyRules(user.rules, 'lider_setor')) && (
                                        <>
                                            <div className="my-3 mx-2 h-px bg-gray-100 dark:bg-neutral-800" />
                                            <SidebarGroup label={t('sidebar.groups.admin')}>
                                                <SidebarMenuButton
                                                    title={t('sidebar.metrics')}
                                                    slug="dashboard/home"
                                                    icon="lucide:layout-dashboard"
                                                    onClick={() => handleNavigate('/dashboard/home')}
                                                />
                                                <SidebarMenuButton
                                                    title={t('sidebar.supervise')}
                                                    slug="dashboard/supervisao"
                                                    icon="lucide:eye"
                                                    onClick={() => handleNavigate('/dashboard/supervisao/lista')}
                                                />

                                                {(verifyRules(user.rules, 'relatorios') || verifyRules(user.rules, 'lider_setor')) && (
                                                    <SidebarExpandableMenu title={t('sidebar.reports')} icon="lucide:file-bar-chart">
                                                        <SidebarMenuButton
                                                            title={t('sidebar.reviews')}
                                                            slug="dashboard/relatorios/chats-avaliacoes"
                                                            icon="lucide:star"
                                                            isSubItem
                                                            onClick={() => handleNavigate('/dashboard/relatorios/chats-avaliacoes')}
                                                        />
                                                        <SidebarMenuButton
                                                            title={t('sidebar.tickets')}
                                                            slug="dashboard/relatorios/chamados"
                                                            icon="lucide:clipboard-list"
                                                            isSubItem
                                                            onClick={() => handleNavigate('/dashboard/relatorios/chamados')}
                                                        />
                                                    </SidebarExpandableMenu>
                                                )}

                                                {user.level === 'root' && (
                                                    <SidebarExpandableMenu title={t('sidebar.ai')} icon="lucide:brain">
                                                        <SidebarMenuButton
                                                            title={t('sidebar.serviceAnalysis')}
                                                            slug="dashboard/ai/analises"
                                                            icon="lucide:sparkles"
                                                            isSubItem
                                                            onClick={() => handleNavigate('/dashboard/ai/analises')}
                                                        />
                                                        {/* <SidebarMenuButton
                                                            title="Chat IA Dashboard"
                                                            slug="dashboard/painel-ia"
                                                            icon="lucide:message-square-plus"
                                                            isSubItem
                                                            onClick={() => handleNavigate('/dashboard/painel-ia')}
                                                        /> */}
                                                    </SidebarExpandableMenu>
                                                )}
                                            </SidebarGroup>
                                        </>
                                    )}

                                    {/* Configuracoes */}
                                    {verifyRules(user.rules, "configuracoes") && (
                                        <>
                                            <div className="my-3 mx-2 h-px bg-gray-100 dark:bg-neutral-800" />
                                            <SidebarMenuButton
                                                title={t('sidebar.settings')}
                                                slug="dashboard/configuracoes"
                                                icon="lucide:settings"
                                                onClick={() => handleNavigate('/dashboard/configuracoes/canais')}
                                            />
                                        </>
                                    )}
                                </nav>
                            </div>

                            {/* Footer com acoes */}
                            <div className="border-t border-gray-100 dark:border-neutral-800 py-3 px-3">
                                {/* Creditos */}
                                <ZuiTooltip
                                    content={
                                        <div className="space-y-1 p-1">
                                            <div className="font-semibold text-xs text-gray-900">{t('navbar.credits')}</div>
                                            <div className="text-[10px] text-gray-500 space-y-0.5">
                                                <div>{t('navbar.balance')}: <span className="font-medium text-gray-700">{credit}</span> {t('navbar.credits').toLowerCase()}</div>
                                                <div>{t('navbar.status')}: <span className={saldo < 0 ? 'text-red-500' : 'text-green-600'}>{saldo < 0 ? t('common:status.negative') : t('common:status.positive')}</span></div>
                                            </div>
                                        </div>
                                    }
                                >
                                    <button
                                        onClick={() => handleNavigate('/dashboard/conta/consumo')}
                                        className={`
                                            w-full flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 mb-2
                                            ${saldo < 0
                                                ? 'bg-red-50 border-red-100 text-red-600 dark:bg-none'
                                                : 'bg-indigo-50 border-indigo-100 text-indigo-600 hover:bg-indigo-100 dark:bg-none'
                                            }
                                        `}
                                    >
                                        <Iconify icon="solar:wallet-bold-duotone" width={18} />
                                        <span className="text-xs font-semibold flex-1 text-left">{credit} {t('navbar.credits').toLowerCase()}</span>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleGetSaldo();
                                            }}
                                            className="p-0.5 rounded-full hover:bg-black/5 disabled:opacity-50"
                                            disabled={isLoadingSaldo}
                                        >
                                            <Iconify
                                                icon="lucide:refresh-cw"
                                                width={12}
                                                className={isLoadingSaldo ? 'animate-spin' : ''}
                                            />
                                        </button>
                                    </button>
                                </ZuiTooltip>

                                {/* Acoes rapidas */}
                                <div className="flex justify-between items-center mb-2">
                                    <Tooltip title={soundNotification === "true" ? t('navbar.soundOn') : t('navbar.soundOff')} placement="top">
                                        <button
                                            onClick={() => changeSoundNotification(soundNotification === "true" ? "false" : "true")}
                                            className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
                                        >
                                            <Iconify
                                                icon={soundNotification === "true" ? "solar:volume-loud-bold-duotone" : "solar:volume-cross-bold-duotone"}
                                                width={20}
                                            />
                                        </button>
                                    </Tooltip>

                                    <NotificationsPopover />
                                    <HelpComponent />
                                </div>

                                <InternetConnectionAlertHeader />

                                {/* Divider */}
                                <div className="my-2 h-px bg-gray-100 dark:bg-neutral-800" />

                                {/* Conta do usuario */}
                                <DropdownAccountNav variant="full" />
                            </div>
                        </aside>
                    </SidebarContext.Provider>
                </Drawer>

                {/* Spacer para o header */}
                <div className="h-14 flex-shrink-0" />
            </SidebarContext.Provider>
        );
    }

    // Desktop: sidebar normal
    return (
        <SidebarContext.Provider value={{ isExpanded }}>
            {sidebarContent}
        </SidebarContext.Provider>
    );
}
